// SeibuCashLedgerPage — 西武信用金庫 出納帳 (2026-06-11)
//
// 西武信用金庫(現金管理/当座/普通)の入出金(wallet_txn)を日付・金額・残高で
// 自動取込し、各行に「用途」「備考」を人が入力でき、処理済フラグを付けられる
// スプレッドシート風の出納帳 UI。用途/備考/処理済は wallet_txn_ledger_annotation
// に保存(freee deal 非連動)。残高は freee 同期値(wallet_txn_cache.balance)。

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Container,
  PageTitle,
  CardBase,
  HStack,
  Button,
  SelectBox,
  Note,
  Breadcrumbs,
  MessageBlock,
  InlineSpinner,
  TextField,
} from '@freee_jp/vibes';
import {
  fetchCashLedger,
  saveCashLedgerAnnotation,
  CashLedgerRow,
  CashLedgerResponse,
} from '../api/client';

// 西武信用金庫の wallet (freee 真値)
const SEIBU_WALLETS = [
  { id: 3838100, name: '西武信用金庫 現金管理' },
  { id: 3838143, name: '西武信用金庫 当座預金' },
  { id: 3838144, name: '西武信用金庫 普通預金' },
];

const yen = (n: number | null | undefined) =>
  n == null ? '—' : `¥${Number(n).toLocaleString('ja-JP')}`;

const currentYm = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const fmtDate = (s: string) => {
  // 'YYYY-MM-DD' -> 'M/D (曜)'
  const d = new Date(s + 'T00:00:00');
  if (isNaN(d.getTime())) return s;
  const w = ['日', '月', '火', '水', '木', '金', '土'][d.getDay()];
  return `${d.getMonth() + 1}/${d.getDate()} (${w})`;
};

const S = {
  page: {
    background: 'linear-gradient(180deg, #f8f7f5 0%, #f1efec 100%)',
    minHeight: '100vh', paddingBottom: '4rem',
  } as React.CSSProperties,
  kpiRow: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '10px', marginTop: '14px',
  } as React.CSSProperties,
  kpiCard: (accent: string): React.CSSProperties => ({
    background: `linear-gradient(135deg, ${accent}14 0%, ${accent}06 100%)`,
    border: `1px solid ${accent}26`, borderRadius: '12px', padding: '12px 16px',
  }),
  kpiLabel: { fontSize: '0.6875rem', color: '#6b6560', fontWeight: 700, letterSpacing: '0.04em' } as React.CSSProperties,
  kpiValue: { fontSize: '1.25rem', fontWeight: 700, marginTop: '4px', fontVariantNumeric: 'tabular-nums' } as React.CSSProperties,
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: '0.8125rem', tableLayout: 'fixed' as const } as React.CSSProperties,
  th: {
    padding: '9px 10px', fontSize: '0.6875rem', fontWeight: 700, color: '#6b6560',
    letterSpacing: '0.04em', borderBottom: '1.5px solid #e5e4e2', background: '#fafaf8',
    textAlign: 'left' as const, whiteSpace: 'nowrap' as const, position: 'sticky' as const, top: 0, zIndex: 1,
  } as React.CSSProperties,
  td: {
    padding: '6px 10px', fontSize: '0.8125rem', borderBottom: '1px solid #f1f0ee',
    verticalAlign: 'middle' as const,
  } as React.CSSProperties,
  amt: { textAlign: 'right' as const, fontVariantNumeric: 'tabular-nums' as const, whiteSpace: 'nowrap' as const, fontWeight: 600 } as React.CSSProperties,
};

interface EditState { purpose: string; memo: string; }

export const SeibuCashLedgerPage: React.FC = () => {
  const [walletableId, setWalletableId] = useState<number>(3838100);
  const [month, setMonth] = useState<string>(() => currentYm());
  const [data, setData] = useState<CashLedgerResponse | null>(null);
  const [edits, setEdits] = useState<Record<number, EditState>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [savedId, setSavedId] = useState<number | null>(null);
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const monthOptions = useMemo(() => {
    const now = new Date();
    const arr: Array<{ name: string; value: string }> = [];
    for (let i = -15; i <= 1; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const y = d.getFullYear(); const m = d.getMonth() + 1;
      arr.push({ name: `${y}年${m}月`, value: `${y}-${String(m).padStart(2, '0')}` });
    }
    return arr.reverse();
  }, []);

  const walletOptions = useMemo(() => SEIBU_WALLETS.map(w => ({ name: w.name, value: String(w.id) })), []);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetchCashLedger(walletableId, month);
      setData(res);
      const e: Record<number, EditState> = {};
      for (const it of res.items) e[it.wallet_txn_id] = { purpose: it.purpose, memo: it.memo };
      setEdits(e);
    } catch (err: any) {
      setError(err?.message || '読み込みに失敗しました');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [walletableId, month]);

  useEffect(() => { load(); }, [load]);

  const flashSaved = useCallback((id: number) => {
    setSavedId(id);
    if (savedTimer.current) clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => setSavedId(null), 1400);
  }, []);

  const persist = useCallback(async (row: CashLedgerRow, next: EditState) => {
    setSavingId(row.wallet_txn_id);
    try {
      await saveCashLedgerAnnotation({
        wallet_txn_id: row.wallet_txn_id,
        walletable_id: walletableId,
        purpose: next.purpose,
        memo: next.memo,
      });
      flashSaved(row.wallet_txn_id);
    } catch (err: any) {
      setError(`保存に失敗: ${err?.message || ''}`);
    } finally {
      setSavingId(null);
    }
  }, [walletableId, flashSaved]);

  const onField = (id: number, key: keyof EditState, value: string | boolean) =>
    setEdits(prev => ({ ...prev, [id]: { ...prev[id], [key]: value } }));

  const items = data?.items || [];

  return (
    <div style={S.page}>
      <Container>
        <div style={{ paddingTop: '18px' }}>
          <Breadcrumbs links={[{ title: 'FREEEMAN', url: '/' }, { title: '西武信用金庫 出納帳' }]} />
        </div>
        <div style={{ marginTop: '6px' }}>
          <PageTitle>西武信用金庫 出納帳</PageTitle>
        </div>
        <Note>
          西武信用金庫の入出金が日付・金額・残高で自動取込されます。各行の「用途」「備考」を入力してください（自動保存）。「済」は freee で仕訳登録・消込が済むと自動で「済」表示になります（手動操作は不要）。
        </Note>

        {/* 操作バー */}
        <div style={{ marginTop: '14px' }}>
          <CardBase>
            <div style={{ padding: '14px 18px' }}>
              <HStack gap={1} alignItems="end">
                <div>
                  <div style={S.kpiLabel}>口座</div>
                  <SelectBox name="wallet" value={String(walletableId)} options={walletOptions}
                    width="large"
                    onChange={(e) => setWalletableId(Number((e.target as HTMLSelectElement).value))} />
                </div>
                <div>
                  <div style={S.kpiLabel}>対象月</div>
                  <SelectBox name="month" value={month} options={monthOptions} width="small"
                    onChange={(e) => setMonth((e.target as HTMLSelectElement).value)} />
                </div>
                <Button onClick={load} disabled={loading}>
                  {loading ? <InlineSpinner isLoading /> : null} 再読込
                </Button>
              </HStack>
            </div>
          </CardBase>
        </div>

        {/* KPI */}
        {data && (
          <div style={S.kpiRow}>
            <div style={S.kpiCard('#8a8580')}>
              <div style={S.kpiLabel}>取引件数</div>
              <div style={S.kpiValue}>{data.total} 件</div>
            </div>
            <div style={S.kpiCard('#2da663')}>
              <div style={S.kpiLabel}>入金合計</div>
              <div style={{ ...S.kpiValue, color: '#1f8a4c' }}>{yen(data.income_total)}</div>
            </div>
            <div style={S.kpiCard('#d68a3c')}>
              <div style={S.kpiLabel}>出金合計</div>
              <div style={{ ...S.kpiValue, color: '#c4732a' }}>{yen(data.expense_total)}</div>
            </div>
            <div style={S.kpiCard('#2e7cf6')}>
              <div style={S.kpiLabel}>月末残高</div>
              <div style={{ ...S.kpiValue, color: '#2667d4' }}>{yen(data.closing_balance)}</div>
            </div>
            <div style={S.kpiCard(data.undone_count > 0 ? '#d68a3c' : '#2da663')}>
              <div style={S.kpiLabel}>未処理</div>
              <div style={S.kpiValue}>{data.undone_count} / {data.total}</div>
            </div>
          </div>
        )}

        {error && <div style={{ marginTop: '12px' }}><MessageBlock>{error}</MessageBlock></div>}

        {/* テーブル */}
        <div style={{ marginTop: '14px' }}>
          <CardBase>
            <div style={{ padding: '4px 6px', overflowX: 'auto' }}>
              {loading && !data ? (
                <div style={{ padding: '40px', textAlign: 'center' }}><InlineSpinner isLoading /> 読み込み中…</div>
              ) : items.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#8a8580' }}>この月の入出金はありません。</div>
              ) : (
                <table style={S.table}>
                  <colgroup>
                    <col style={{ width: '92px' }} />
                    <col style={{ width: '210px' }} />
                    <col style={{ width: '110px' }} />
                    <col style={{ width: '110px' }} />
                    <col style={{ width: '120px' }} />
                    <col style={{ width: '220px' }} />
                    <col style={{ width: '240px' }} />
                    <col style={{ width: '54px' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th style={S.th}>日付</th>
                      <th style={S.th}>摘要（銀行明細）</th>
                      <th style={{ ...S.th, textAlign: 'right' }}>入金</th>
                      <th style={{ ...S.th, textAlign: 'right' }}>出金</th>
                      <th style={{ ...S.th, textAlign: 'right' }}>残高</th>
                      <th style={S.th}>用途</th>
                      <th style={S.th}>備考</th>
                      <th style={{ ...S.th, textAlign: 'center' }}>済</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => {
                      const e = edits[it.wallet_txn_id] || { purpose: '', memo: '' };
                      const rowBg = it.is_done ? '#f4faf6' : (it.entry_side === 'income' ? '#fbfdfb' : '#ffffff');
                      return (
                        <tr key={it.wallet_txn_id} style={{ background: rowBg }}>
                          <td style={{ ...S.td, whiteSpace: 'nowrap', color: '#555' }}>{fmtDate(it.date)}</td>
                          <td style={{ ...S.td, color: '#777', fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={it.description || ''}>
                            {it.description || '—'}
                          </td>
                          <td style={{ ...S.td, ...S.amt, color: '#1f8a4c' }}>
                            {it.entry_side === 'income' ? yen(it.amount) : ''}
                          </td>
                          <td style={{ ...S.td, ...S.amt, color: '#c4732a' }}>
                            {it.entry_side === 'expense' ? yen(it.amount) : ''}
                          </td>
                          <td style={{ ...S.td, ...S.amt, color: '#3a3530' }}>{yen(it.balance)}</td>
                          <td style={S.td}>
                            <TextField width="full" small value={e.purpose} placeholder="用途（例: 小口現金 / 現金給与）"
                              onChange={(ev: any) => onField(it.wallet_txn_id, 'purpose', ev.target.value)}
                              onBlur={() => persist(it, edits[it.wallet_txn_id])} />
                          </td>
                          <td style={S.td}>
                            <HStack gap={0.5} alignItems="center">
                              <div style={{ flex: 1 }}>
                                <TextField width="full" small value={e.memo} placeholder="備考"
                                  onChange={(ev: any) => onField(it.wallet_txn_id, 'memo', ev.target.value)}
                                  onBlur={() => persist(it, edits[it.wallet_txn_id])} />
                              </div>
                              <span style={{ width: '16px', fontSize: '0.75rem', color: '#2da663' }}>
                                {savingId === it.wallet_txn_id ? '…' : savedId === it.wallet_txn_id ? '✓' : ''}
                              </span>
                            </HStack>
                          </td>
                          <td style={{ ...S.td, textAlign: 'center' }}>
                            <span title="freee の消込状況（自動）" style={{
                              display: 'inline-block', padding: '2px 9px', borderRadius: '10px',
                              fontSize: '0.6875rem', fontWeight: 700,
                              background: it.is_done ? '#d3efdc' : '#f0eeec',
                              color: it.is_done ? '#239e5b' : '#a39e98',
                            }}>{it.is_done ? '済' : '未'}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </CardBase>
        </div>

        <div style={{ marginTop: '10px', fontSize: '0.6875rem', color: '#9a958f' }}>
          残高は freee 同期値（{data?.walletable_name || '西武信用金庫'}）。「済」は freee の消込状況（未消込=未／消込済=済）を自動反映。用途・備考は自動保存されます。
        </div>
      </Container>
    </div>
  );
};

export default SeibuCashLedgerPage;
